import { useRef, useEffect, useCallback, useId, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSearchAutocomplete } from '../../hooks/useSearchAutocomplete';

interface SearchBarProps {
  initialQuery?: string;
  className?: string;
  onSearch?: (query: string) => void;
}

export default function SearchBar({ initialQuery = '', className = '', onSearch }: SearchBarProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('search');
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const dir = i18n.dir();

  const { query, setQuery, suggestions, isLoading, isOpen, setIsOpen } = useSearchAutocomplete(lang);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // /search mounts the filter panel twice (sidebar and phone sheet), so the ids
  // must be unique per instance or aria-controls points at the wrong list.
  const instanceId = useId();
  const inputId = `site-search-${instanceId.replace(/:/g, '')}`;
  const listId = `${inputId}-suggestions`;

  // Sync initialQuery into the input on mount / when it changes externally
  useEffect(() => {
    if (initialQuery && initialQuery !== query) {
      setQuery(initialQuery);
    }
    // Only run on mount / initialQuery change – not on every query change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setIsOpen]);

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      setIsOpen(false);
      if (onSearch) {
        onSearch(query.trim());
      } else if (query.trim()) {
        navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      } else {
        navigate('/search');
      }
    },
    [navigate, query, setIsOpen, onSearch],
  );

  const handleSuggestionClick = useCallback(
    (suggestion: { slug: string; title: string }) => {
      setIsOpen(false);
      if (onSearch) {
        // On search page: use the suggestion title as a search query
        setQuery(suggestion.title);
        onSearch(suggestion.title);
      } else {
        // On other pages: navigate to the property detail
        navigate(`/property/${suggestion.slug}`);
      }
    },
    [navigate, setIsOpen, onSearch, setQuery],
  );

  return (
    <div ref={containerRef} className={`relative ${className}`} dir={dir}>
      <form onSubmit={handleSubmit} role="search">
        <div className="relative flex items-center">
          <i className="ri-search-line pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-lg text-[var(--pv-color-text-muted)] z-10" aria-hidden />
          <input
            ref={inputRef}
            id={inputId}
            name="q"
            type="search"
            autoComplete="off"
            role="combobox"
            aria-label={t('search.filters.searchLabel', { defaultValue: 'Search properties' })}
            aria-expanded={isOpen && suggestions.length > 0}
            aria-controls={listId}
            aria-autocomplete="list"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => { if (suggestions.length > 0) setIsOpen(true); }}
            placeholder={t('search.filters.searchPlaceholder', { defaultValue: 'Search properties…' })}
            className="w-full rounded-2xl border border-[var(--pv-border-soft)] bg-white py-2.5 ps-10 pe-12 text-sm text-[var(--pv-color-text-muted)] placeholder:text-[var(--pv-color-text-muted)]/60 shadow-sm transition focus-visible:border-[#004D40] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#004D40]/20"
          />
          <button
            type="submit"
            className="absolute end-2 top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded-xl bg-[var(--pv-color-primary)] text-white shadow transition hover:opacity-90"
            aria-label={t('search.filters.searchLabel', { defaultValue: 'Search' })}
          >
            {isLoading ? (
              <i className="ri-loader-4-line animate-spin text-sm" aria-hidden />
            ) : (
              <i className="ri-arrow-right-line text-sm rtl:-scale-x-100" aria-hidden />
            )}
          </button>
        </div>
      </form>

      {isOpen && suggestions.length > 0 && (
        <div
          id={listId}
          role="listbox"
          className="absolute z-50 mt-1 w-full rounded-xl border border-[var(--pv-border-soft)] bg-white py-1.5 shadow-lg"
        >
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.id}
              type="button"
              role="option"
              aria-selected={false}
              onClick={() => handleSuggestionClick(suggestion)}
              className="flex w-full items-start gap-3 px-4 py-2.5 text-start transition hover:bg-[rgba(0,77,64,0.06)]"
            >
              <i className="ri-building-line mt-0.5 flex-shrink-0 text-base text-[var(--pv-color-primary)]" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-[var(--pv-color-text)]">
                  {suggestion.title}
                </p>
                <p className="truncate text-xs text-[var(--pv-color-text-muted)]">
                  {suggestion.city}
                  {suggestion.propertyType ? ` · ${suggestion.propertyType}` : ''}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
